/**
 * Hook for fetching available models from platform providers
 * 
 * Fetches models from GET /api/v1/models/available and provides
 * utilities for grouping by category and finding defaults.
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import type { AvailableModel, ModelCategory } from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const modelKeys = {
  available: ['models', 'available'] as const,
  byCategory: (category: ModelCategory) => [...modelKeys.available, category] as const,
};

// =============================================================================
// Types for API response (grouped by category)
// =============================================================================

interface AvailableModelsResponse {
  data: Record<string, AvailableModel[]>;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch all available models from configured providers.
 * 
 * The API returns models grouped by category:
 *   {"data": {"main": [...], "router": [...], "tools": [...], "embedding": [...]}}
 * 
 * This hook flattens them into a single array with `category` set on each model.
 */
export function useAvailableModels() {
  return useQuery({
    queryKey: modelKeys.available,
    queryFn: async () => {
      const response = await apiClient.get<AvailableModelsResponse>(
        '/api/v1/models/available'
      );
      const grouped = response.data.data;

      // Flatten grouped dict into a single array, injecting category
      const flat: AvailableModel[] = [];
      for (const [category, models] of Object.entries(grouped)) {
        for (const model of models) {
          flat.push({
            ...model,
            category: category as ModelCategory,
          });
        }
      }
      return flat;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Group models by category
 */
export interface ModelsByCategory {
  main: AvailableModel[];
  router: AvailableModel[];
  tools: AvailableModel[];
  embedding: AvailableModel[];
}

/**
 * Get models grouped by category
 */
export function useModelsByCategory() {
  const { data: models, ...rest } = useAvailableModels();

  const grouped: ModelsByCategory = {
    main: models?.filter((m) => m.category === 'main') || [],
    router: models?.filter((m) => m.category === 'router') || [],
    tools: models?.filter((m) => m.category === 'tools') || [],
    embedding: models?.filter((m) => m.category === 'embedding') || [],
  };

  return { grouped, ...rest };
}

/**
 * Default model IDs for each category
 */
export interface DefaultModels {
  main?: string;
  router?: string;
  tools?: string;
}

/**
 * Get default models for each category
 */
export function useDefaultModels() {
  const { data: models, ...rest } = useAvailableModels();

  const defaults: DefaultModels = {
    main: models?.find((m) => m.category === 'main' && m.is_default)?.id,
    router: models?.find((m) => m.category === 'router' && m.is_default)?.id,
    tools: models?.find((m) => m.category === 'tools' && m.is_default)?.id,
  };

  return { defaults, ...rest };
}
